import { Stripe, StripeElements } from '@stripe/stripe-js';
import { ClientEvents, ClientTrackableEvents } from 'common/events/ClientEvents';
import { SpiceLevel } from 'common/SpiceLevels';
import { DateTime } from 'luxon';
import { UseControllerReturn } from 'react-hook-form';
import {
  CuisineCategory,
  DietaryRestriction,
  DietType,
  DishTag,
  FlaggableIngredient,
  FoodItemAvailability,
  FoodItemCapacityForDeliveryDate,
  FoodItemCustomization,
  FoodItemMacros,
  FoodItemQuantity,
  HouseholdGoals,
  IngredientPriority,
  Maybe,
  MealPlanDishCardFoodItemFragment,
  MealPlanFoodItemUpsellFragment,
  MealPlanFoodType,
  MealPlanOrderStatus,
  MealPlanPreferences,
  MealPlanPromoCodeAppliedFragment,
  MealPlanShefFragment,
  MealPlanType,
  MerchandisingTag,
  Packaging,
  Protein,
  PublicShef,
  QueryGetMealPlanningMenuArgs,
  SideCategory,
  TasteProfile,
} from 'src/gqlReactTypings.generated.d';

export enum DishCardDisplayMode {
  Carousel = 'Carousel',
  Grid = 'Grid',
}

export type ZipCodeOnly = {
  zipCode: string | undefined | null;
};

export interface MealPlanFoodItem {
  __typename?: 'FoodItem';
  id: number;
  createdAt?: string | null;
  name?: string | null;
  imageUrl?: string | null;
  averageRating: number;
  ratingCount: number;
  cuisineCategories: (Pick<CuisineCategory, 'title' | 'id' | 'type' | 'parentId'> & { rootCuisineId?: string })[];
  sideCategories?: Pick<SideCategory, 'id' | 'name' | 'displayName' | 'displayOrder'>[];
  shef: Pick<PublicShef, 'publicName' | 'id' | 'firstName' | 'imageUrl'>;
  spiceLevel: SpiceLevel;
  portion?: string | null;
  servingSize?: string | null;
  ingredients?: string | null;
  ingredientsFlagged?: FlaggableIngredient[];
  customIngredientsFlagged?: string[] | null;
  merchandisingTags?: MerchandisingTag[];
  isFlexibleSides?: boolean | null;
  isMainsPlusSides?: boolean | null;
  description?: string | null;
  price: number;
  vegetarian?: boolean | null;
  vegan?: boolean | null;
  glutenFree?: boolean;
  dairyFree?: boolean;
  isHalal?: boolean;
  isOrganic?: boolean;
  isSeedOilFree?: boolean | null;
  tags?: DishTag[] | null;
  lowFodmap?: boolean | null;
  paleo?: boolean | null;
  whole30?: boolean | null;
  kosher?: boolean | null;
  packaging?: Packaging;
  availability?: Maybe<Array<FoodItemAvailability>>;
  slug?: string;
  spiceLevelCustomizations?: Maybe<Array<FoodItemCustomization>>;
  servingSizeCustomizations?: Maybe<Array<FoodItemCustomization>>;
  topPositiveTags?: string[] | null;
  upsell?: MealPlanFoodItemUpsellFragment | null;
  mealPlanFoodType?: MealPlanFoodType | null;
  prefabFoodItemId?: number | null;
  macros?: FoodItemMacros | null;
}

export interface MealPlanShefSegment {
  foodItems: MealPlanFoodItem[];
  shef: MealPlanShefFragment;
  shefRemainingAvailability: number;
}

/** A list of rank groups of shef segments
 * First is same shef as the cart,
 * Second is shefs matching the 'primary cuisine' of an item in cart
 * Third is the rest of the shefs
 */
export type ShefSegmentRankGroups = MealPlanShefSegment[][];

export interface MealPlanPreferencesStorageData {
  deliveryDate: string;
  cuisinePreferences?: string[] | null;
  dietType?: DietType;
  replacementDietaryRestrictions?: DietaryRestriction[];
  menuDietaryRestrictions?: DietaryRestriction[];
  preferredProteins?: Protein[] | null;
  mealPlanTypeId?: number | undefined;
  numAdults?: number;
  numLittleEaters?: number;
  numBigEaters?: number;
  ingredientPriorities?: IngredientPriority[] | null;
  householdGoals?: HouseholdGoals[] | null;
}

export interface MealSelectionAndCheckoutProps {
  userPreferences: MealPlanPreferencesStorageData;
  mains: MealPlanShefSegment[];
  sides: MealPlanShefSegment[];
  refetchMealPlanningMenu: (variables?: QueryGetMealPlanningMenuArgs) => void;
}

export interface MealPlanForUserData {
  nextDeliveryDate: string;
  numMeals: number;
  numServings: number;
  subscription: MealPlanPreferences;
  mealPlanOrderStatus: MealPlanOrderStatus;
}

export interface MealEditProps {
  foodItemCapacityForDeliveryDate: FoodItemCapacityForDeliveryDate[];
  cartLineItemUnits: FoodItemQuantity[];
  mainDishes: Pick<MealPlanDishCardFoodItemFragment, 'id'>[];
  mealPlanGroupOrderId?: string | null;
  mealPlanOrderStatus: MealPlanOrderStatus;
  mains: MealPlanShefSegment[];
  sides: MealPlanShefSegment[];
  sideDishes: Pick<MealPlanDishCardFoodItemFragment, 'id'>[];
  sidesPerWeek: number;
  subscription: Pick<
    MealPlanPreferences,
    'numMeals' | 'numServings' | 'nextDeliveryDate' | 'id' | 'isReusablePackagingPlan'
  >;
  tasteProfile: TasteProfile;
  refetchMealPlanningMenu: (variables?: QueryGetMealPlanningMenuArgs) => void;
}

type Selection = {
  [id: string]: number;
};

export interface DishSelectionFormValues {
  dishes: Selection;
  sides: Selection;
}

export interface MealPlanAddSidesProps {
  sideSelectionInput: UseControllerReturn<Partial<DishSelectionFormValues>, 'sides'>;
  totalSides?: number;
  requiredNumSides?: number;
  editing?: boolean;
  isPastCutoffDate?: boolean;
  heading?: string;
}

export interface MealPlanReviewOrderProps {
  mealPlanType: MealPlanType;
  mealPlanPromoCodeApplied?: MealPlanPromoCodeAppliedFragment | null;
  previousStep: () => void;
  deliveryDate: string;
  deliveryTimeWindow?: { deliveryTimeWindowStart: number; deliveryTimeWindowEnd: number };
  showComprehensionSection?: boolean;
  isFirstOrder?: boolean;
  isVariableServiceFeeTreatment?: boolean;
  nativePayAvailable: 'apple' | 'google' | 'none';
  nativePayResolved: boolean;
  tipCents: number;
  onTipChange?: (tipCents: number) => void;
}

export interface WrappedMealPlanFinalizeProps {
  mealPlanType: MealPlanType;
  userPreferences: MealPlanPreferencesStorageData;
  paymentDetailsRef: React.RefObject<HTMLParagraphElement>;
  previousStep: () => void;
  elements: StripeElements;
  stripe: Stripe;
  expressCheckoutClientSecret: string | null;
  refreshExpressCheckoutSetup: () => void;
}

export interface MealPlanFinalizeProps extends WrappedMealPlanFinalizeProps {
  applePayAvailable: boolean;
  googlePayAvailable: boolean;
}

export interface MealPlanConsolidatedCheckoutProps {
  mealPlanType: MealPlanType;
  userPreferences: MealPlanPreferencesStorageData;
  previousStep: () => void;
  elements: StripeElements;
  stripe: Stripe;
  expressCheckoutClientSecret: string | null;
  refreshExpressCheckoutSetup: () => void;
}

export interface DishSelectionSummaryProps {
  deliveryDateTime: DateTime | null;
  numEntrees: number;
  numServings: number;
}

export type CheckoutInputStartedName =
  ClientTrackableEvents[ClientEvents.MEAL_PLANNING_MVP_CHECKOUT_INPUT_STARTED]['input'];
